import { useEffect, useState } from 'react';
import { Calendar, Send, Target } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { campanaService } from '../services/campanaService';
import { aporteCampanaService } from '../services/aporteCampanaService';
import { miembroService } from '../services/miembroService';
import type { Campaña, AporteCampaña, Miembro } from '../lib/supabase';
import { puedeOperarDinero } from '../utils/permissions';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useAlCambiarClave } from '../hooks/useAlCambiarClave';
import { VolverButton } from '../components/ui/VolverButton';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { FormErrorAlert } from '../components/ui/FormErrorAlert';
import { useToast } from '../components/ui/Toast';
import { CompromisoModal } from '../components/CompromisoModal';
import { CompromisosCampanaList } from '../components/CompromisosCampanaList';
import { AporteCampanaModal } from '../components/AporteCampanaModal';
import { NotificarEnvioModal } from '../components/NotificarEnvioModal';
import { EnviosCampanaPanel } from '../components/EnviosCampanaPanel';

interface Props {
  readonly campañaId: string;
  readonly onBack: () => void;
  readonly readOnly?: boolean;
}
interface Detalle {
  readonly campana: Campaña | null;
  readonly aportes: AporteCampaña[];
  readonly miembros: Miembro[];
  readonly historicos: ReadonlySet<string>;
}
async function cargarDetalle(id: string): Promise<Detalle> {
  const [campana, aportes, pagos] = await Promise.all([
    campanaService.getById(id), aporteCampanaService.getByCampana(id), aporteCampanaService.getPagosDeCampana(id),
  ]);
  const miembros = campana ? await miembroService.getByLumisial(campana.lumisial_id) : [];
  const historicos = new Set(pagos.flatMap(p => p.aporte_campana_id ? [p.aporte_campana_id] : []));
  return { campana, aportes, miembros, historicos };
}
type Cambio = { readonly tipo: 'eliminar'; readonly id: string }
  | { readonly tipo: 'estado'; readonly estado: 'en_curso' | 'finalizada' };

export function DetalleCampanaPage({ campañaId, onBack, readOnly: solicitado = false }: Props) {
  const { usuario } = useAuth();
  const toast = useToast();
  const [detalle, setDetalle] = useState<Detalle | null>(null);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [aporte, setAporte] = useState<AporteCampaña | undefined>();
  const [envio, setEnvio] = useState(false);
  const [cambio, setCambio] = useState<Cambio | null>(null);
  useAlCambiarClave(`${campañaId}:${revision}`, () => { setLoading(true); });
  useEffect(() => {
    let vigente = true;
    cargarDetalle(campañaId).then(data => {
      if (vigente) {
        setDetalle(data); setLoading(false); setError('');
        setAporte(anterior => anterior ? data.aportes.find(a => a.id === anterior.id) : undefined);
      }
    }).catch(() => {
      if (vigente) { setError('No pudimos cargar la campaña y su contabilidad.'); setLoading(false); }
    });
    return () => { vigente = false; };
  }, [campañaId, revision]);
  const recargar = () => setRevision(r => r + 1);
  const campana = detalle?.campana;
  const readOnly = solicitado || !campana || !puedeOperarDinero(usuario, {
    tipo: 'lumisial', id: campana.lumisial_id, diocesisId: null, provinciaEclesiasticaId: null,
  });
  const confirmarCambio = async () => {
    if (!cambio || readOnly) return;
    try {
      if (cambio.tipo === 'eliminar') await aporteCampanaService.delete(cambio.id);
      else await campanaService.updateEstado(campañaId, cambio.estado);
      recargar();
    } catch (error_: unknown) {
      toast.error(error_ instanceof Error ? error_.message : 'No pudimos completar la operación.');
    }
  };
  if (loading) return <p className="p-8 text-center text-slate-500">Cargando campaña…</p>;
  if (!detalle || !campana) return <div className="space-y-4">
    <VolverButton onClick={onBack} /><FormErrorAlert error={error || 'Campaña no encontrada.'} />
  </div>;
  const abierta = ['planificacion', 'en_curso'].includes(campana.estado);
  const distribuida = campana.campana_master_id !== undefined && campana.campana_master_id !== null;
  const puedeRendir = distribuida && ['en_curso', 'finalizada'].includes(campana.estado);
  const totalComprometido = detalle.aportes.reduce((sum, a) => sum + a.monto_comprometido, 0);
  const totalEntregado = detalle.aportes.reduce((sum, a) => sum + a.monto_aportado, 0);
  return <div className="space-y-6">
    <div className="flex items-center gap-4"><VolverButton onClick={onBack} />
      <div><h2 className="text-2xl font-bold text-slate-800">{campana.nombre}</h2>
        <p className="text-sm text-slate-500">{campana.estado === 'finalizada' ? 'Convocatoria cerrada; contabilidad habilitada' : campana.estado}</p>
      </div>
    </div>
    <FormErrorAlert error={error} />
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <p className="mb-3 text-slate-600">{campana.descripcion}</p>
      <p className="flex items-center gap-2 text-sm text-slate-500"><Calendar className="h-4 w-4" />
        {formatDate(campana.fecha_inicio)}{campana.fecha_fin && ` — ${formatDate(campana.fecha_fin)}`}
      </p>
      <p className="mt-4 flex items-center gap-2"><Target className="h-4 w-4" />Objetivo: <strong>{formatCurrency(campana.objetivo_recaudacion)}</strong></p>
      <div className="mt-5 flex flex-wrap gap-3">
        {!readOnly && campana.estado === 'planificacion' && <button onClick={() => setCambio({ tipo: 'estado', estado: 'en_curso' })}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white">Iniciar campaña</button>}
        {!readOnly && campana.estado === 'en_curso' && <button onClick={() => setCambio({ tipo: 'estado', estado: 'finalizada' })}
          className="rounded-lg bg-slate-600 px-4 py-2 text-sm text-white">Finalizar convocatoria</button>}
        {!readOnly && puedeRendir && <button onClick={() => setEnvio(true)}
          className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm text-white"><Send className="h-4 w-4" />Notificar envío</button>}
      </div>
    </section>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {[['Comprometido', totalComprometido], ['Entregado (dinero y especie)', totalEntregado],
        ['Pendiente', totalComprometido - totalEntregado]].map(([label, value]) => <div key={label}
          className="rounded-lg border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">{label}</p><p className="mt-2 text-xl font-bold">{formatCurrency(Number(value))}</p>
        </div>)}
    </div>
    <CompromisosCampanaList aportes={detalle.aportes} miembros={detalle.miembros} historicos={detalle.historicos}
      readOnly={readOnly} convocatoriaAbierta={abierta} onNuevo={() => setNuevo(true)}
      onEntrega={setAporte} onEliminar={id => setCambio({ tipo: 'eliminar', id })} />
    {distribuida && <EnviosCampanaPanel owner={{ tipo: 'lumisial', id: campana.lumisial_id, nombre: 'Lumisial' }}
      readOnly={readOnly} campanaId={campana.id} campanaMasterId={campana.campana_master_id} onRefresh={recargar} />}
    {!readOnly && abierta && <CompromisoModal isOpen={nuevo} onClose={() => setNuevo(false)} onSuccess={recargar}
      campañaId={campañaId} miembros={detalle.miembros} />}
    <AporteCampanaModal isOpen={aporte !== undefined} onClose={() => setAporte(undefined)} onSuccess={recargar}
      aporte={aporte} readOnly={readOnly} />
    {!readOnly && <NotificarEnvioModal isOpen={envio} onClose={() => setEnvio(false)} onSuccess={recargar}
      campanaId={campana.id} lumisialId={campana.lumisial_id} />}
    <ConfirmDialog isOpen={cambio !== null} onClose={() => setCambio(null)} onConfirm={confirmarCambio}
      title="Confirmar operación" message="¿Quieres completar esta operación? Finalizar cierra la convocatoria y permite terminar la contabilidad."
      confirmText="Confirmar" cancelText="Cancelar" type={cambio?.tipo === 'eliminar' ? 'delete' : 'archive'} />
  </div>;
}
